import React from 'react';

import { RxMagicWand } from 'react-icons/rx';

import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Avatar,
  AvatarGroup,
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';

import { Team } from '../types/thematicGroups';

interface ThematicGroupsMobileProps {
  teams: Team[];
  commonTeams: Team[];
  onOpenMembersModal: (team: Team) => void;
}

const ThematicGroupsMobile: React.FC<ThematicGroupsMobileProps> = ({
  teams,
  commonTeams,
  onOpenMembersModal,
}) => {
  const theme = useTheme();

  const isCommonTeam = (team: Team) => {
    return commonTeams.some(commonTeam => commonTeam.uuid === team.uuid);
  };

  return (
    <Stack spacing={2}>
      {teams.map(team => {
        const isCommon = isCommonTeam(team);
        const commonConnections = team.in_common_connections;
        const totalCommon = commonConnections.length;

        return (
          <Card
            key={team.uuid}
            sx={{
              borderLeft: isCommon ? `2px solid ${theme.palette.primary.main}` : 'none',
              borderRadius: 2,
              backgroundColor: theme.palette.background.default,
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Stack spacing={1}>
                {/* Header com nome e ícone */}
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center" gap={1}>
                    {isCommon && <RxMagicWand size={16} color={theme.palette.primary.main} />}
                    <Typography variant="subtitle1" fontWeight="bold">
                      {team.name}
                    </Typography>
                  </Box>
                </Box>

                {/* Total de membros */}
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" color="text.primary">
                    Total de membros:
                  </Typography>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="body2" fontWeight="bold">
                      {team.total_members}
                    </Typography>
                    <IconButton
                      onClick={() => onOpenMembersModal(team)}
                      size="small"
                      sx={{
                        color: theme.palette.primary.main,
                        '&:hover': {
                          backgroundColor: theme.palette.primary.main + '20',
                        },
                      }}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Box>
                </Box>

                {/* Em comum */}
                <Box>
                  <Typography variant="body2" color="text.primary" fontWeight="bold" mb={1}>
                    Em comum:
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    {totalCommon > 0 ? (
                      <>
                        <AvatarGroup
                          max={6}
                          sx={{ '& .MuiAvatar-root': { width: 32, height: 32 } }}
                        >
                          {commonConnections.slice(0, 6).map(connection => (
                            <Avatar
                              key={connection.id}
                              src={connection.photo_url}
                              alt={connection.name}
                              sx={{ width: 32, height: 32 }}
                            >
                              {connection.name.charAt(0)}
                            </Avatar>
                          ))}
                        </AvatarGroup>
                        {totalCommon > 6 && (
                          <Chip
                            label={`+${totalCommon - 6}`}
                            size="small"
                            sx={{
                              backgroundColor: theme.palette.primary.main,
                              color: theme.palette.background.paper,
                              fontSize: '0.75rem',
                              height: 24,
                              minWidth: 24,
                            }}
                          />
                        )}
                      </>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontStyle: 'italic' }}
                      >
                        Nenhuma conexão em comum
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );
};

export default ThematicGroupsMobile;
